import React, { useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/api';
import { useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { FaDownload, FaExclamationCircle } from 'react-icons/fa';

const ViewClassResults = () => {
  const { classID, form } = useParams();
  const [termID, setTermID] = useState('');
  const [year, setYear] = useState('');
  const [results, setResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleTermChange = (event) => {
    setTermID(event.target.value);
  };

  const handleYearChange = (event) => {
    setYear(event.target.value);
  };

  const fetchResults = async () => {
    setHasSearched(true);
    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/getallresults`, {
        TermID: termID,
        Year: year,
        ClassID: classID,
        form: form,
      });
      setResults(response.data);
    } catch (error) {
      console.error('Error fetching results:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = async (studentRegNumber, studentName, studentSurname) => {
    try {
      const response = await axios.post(`${BASE_URL}/getsubjectresults/`, {
        termID,
        year,
        studentRegNumber,
        classID,
        form,
      });
      
      // Find the student's positions from the results array
      const studentResult = results.find(r => r.RegNumber === studentRegNumber);
      
      setSelectedStudent({
        ...response.data,
        RegNumber: studentRegNumber,
        Name: studentName,
        Surname: studentSurname,
        classPosition: studentResult?.ClassPosition || 'N/A',
        formPosition: studentResult?.FormPosition || 'N/A'
      });
      setModalIsOpen(true);
    } catch (error) {
      console.error('Error fetching student results:', error);
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedStudent(null);
  };

  const downloadResultsPDF = () => {
    const input = document.getElementById('modal-content');
    html2canvas(input).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF();
      pdf.addImage(imgData, 'PNG', 0, 0);
      pdf.save('student_results.pdf');
    });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">Class Results</h2>
        <div className="flex flex-wrap justify-between items-center gap-4">
          <select
            value={termID}
            onChange={handleTermChange}
            className="border border-gray-300 rounded px-4 py-2 w-full sm:w-auto focus:outline-none focus:ring focus:ring-pink-200"
          >
            <option value="" disabled>Select Term</option>
            <option value="1">Term 1</option>
            <option value="2">Term 2</option>
            <option value="3">Term 3</option>
          </select>
          <input
            type="number"
            placeholder="Enter year..."
            value={year}
            onChange={handleYearChange}
            min="2000"
            max="2100"
            className="border border-gray-300 rounded px-4 py-2 w-full sm:w-auto focus:outline-none focus:ring focus:ring-pink-200"
          />
          <button
            onClick={fetchResults}
            disabled={loading}
            className="bg-pink-600 text-white px-6 py-2 rounded hover:bg-pink-700 transition-colors w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : 'Fetch Results'}
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
        </div>
      )}

      {!loading && results.length === 0 && hasSearched && (
        <div className="flex flex-col items-center justify-center mt-16">
          <FaExclamationCircle className="text-red-500 text-6xl mb-4" />
          <p className="text-lg font-semibold">No results found for the selected term and year.</p>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full">
            <thead>
              <tr className="bg-pink-600 text-white">
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Class Position</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Form Position</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">RegNumber</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Surname</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Total Mark</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {results.map((result) => (
                <tr key={result.GradeLevelResultID} className="hover:bg-pink-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{result.ClassPosition}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{result.FormPosition}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{result.RegNumber}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{result.Name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{result.Surname}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{result.TotalMark}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      className="bg-pink-600 text-white px-4 py-2 rounded hover:bg-pink-700 transition-colors"
                      onClick={() => openModal(result.RegNumber, result.Name, result.Surname)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalIsOpen && selectedStudent && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-y-auto scrollbar-hide">
            <div id="modal-content">
              <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-700">Student Results</h2>
                <FaDownload
                  onClick={downloadResultsPDF}
                  className="cursor-pointer text-pink-600 hover:text-pink-700 transition-colors"
                  size={24}
                />
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6 bg-gray-50 p-4 rounded-lg">
                <div className="space-y-2">
                  <p className="text-gray-700">
                    <span className="font-semibold">Name:</span> {selectedStudent.Name} {selectedStudent.Surname}
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">RegNumber:</span> {selectedStudent.RegNumber}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-gray-700">
                    <span className="font-semibold">Class Position:</span> {selectedStudent.classPosition}
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Form Position:</span> {selectedStudent.formPosition}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-gray-700">
                    <span className="font-semibold">Year:</span> {year}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-gray-700">
                    <span className="font-semibold">Term:</span> {termID}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-pink-600 text-white">
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Subject</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Final Mark</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Grade</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Comment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Paper Marks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {selectedStudent.subjectResults.map((subject) => (
                      <tr key={subject.ResultID} className="hover:bg-pink-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{subject.SubjectName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{subject.AverageMark}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{subject.Grade}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{subject.Comment}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {subject.PaperMarks.map((paper, idx) => (
                            <div key={idx} className="mb-1">
                              {paper.PaperName}: {paper.Mark}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <button 
              onClick={closeModal} 
              className="mt-6 bg-pink-600 text-white px-6 py-2 rounded hover:bg-pink-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Add this CSS to your global styles or as a style tag
const styles = `
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

export default ViewClassResults;
